import { useCallback, useMemo } from 'react';
import { matchPath, useLocation } from 'react-router';

import useProductMode from './useProductMode';
import {
  ROUTE_ASSIST_INSIGHTS_INSIGHT_CARD,
  ROUTE_ASSIST_INSIGHTS_INSIGHT_CARD_VIEW,
  ROUTE_SOLVE_INSIGHTS_INSIGHT_CARD,
  ROUTE_SOLVE_INSIGHTS_INSIGHT_CARD_VIEW,
} from 'src/components/app/routes/routeDefinitions';
import { useGetChannelOptionsQuery } from 'src/services/insights';

const DEFAULT_CHANNEL = 'widget';

export default function useNormalizeChannels(
  forceNormalize = false,
): (channels: string[]) => string[] {
  const { data } = useGetChannelOptionsQuery();
  const defaultChannels = useMemo(() => {
    const channelOptions = data?.data || [];
    return channelOptions.length > 0
      ? channelOptions.map(({ value }) => value)
      : [DEFAULT_CHANNEL];
  }, [data?.data]);
  const [productMode] = useProductMode();

  const { pathname } = useLocation();

  const insightCardViewPathMatch =
    matchPath(ROUTE_SOLVE_INSIGHTS_INSIGHT_CARD_VIEW, pathname) ||
    matchPath(ROUTE_ASSIST_INSIGHTS_INSIGHT_CARD_VIEW, pathname);

  const insightCardPathMatch =
    matchPath(ROUTE_SOLVE_INSIGHTS_INSIGHT_CARD, pathname) ||
    matchPath(ROUTE_ASSIST_INSIGHTS_INSIGHT_CARD, pathname);

  const normalizeChannels = useCallback(
    (channels: string[]) => {
      // forceNormalize is an explicit caller override (e.g. the card view
      // expanding the card's own saved channels), so it wins over the
      // route-based branches below — otherwise the card-view branch would
      // return the card's channels unexpanded, dropping the "widget"
      // umbrella's sibling channels (sunshine, ...) where data lives.
      if (forceNormalize) {
        return expandWidgetUmbrella(channels, defaultChannels);
      }

      if (insightCardViewPathMatch) {
        return channels.length > 0 ? channels : defaultChannels;
      }

      if (insightCardPathMatch) {
        return expandWidgetUmbrella(channels, defaultChannels);
      }

      if (productMode === 'widget') {
        const normalizedChannels = channels.filter(isWidgetRelatedChannel);
        if (normalizedChannels.length > 0) {
          return normalizedChannels;
        }
        const widgetRelatedDefaults = defaultChannels.filter(
          isWidgetRelatedChannel,
        );
        return widgetRelatedDefaults.length > 0
          ? widgetRelatedDefaults
          : [DEFAULT_CHANNEL];
      }

      // The Assist product surface is backed by the `playbook` conversation
      // channel in the backend enum — `assist` is not a valid channel value.
      if (productMode === 'assist') {
        return ['playbook'];
      }

      return [productMode];
    },
    [
      defaultChannels,
      forceNormalize,
      insightCardPathMatch,
      insightCardViewPathMatch,
      productMode,
    ],
  );

  return normalizeChannels;
}

export function useNormalizeChannelsForBuildQueryFromMultiSelected(): (
  channels: string[],
) => string[] {
  const normalizeChannels = useNormalizeChannels();

  return useCallback(
    (channels: string[]) => {
      const normalizedChannels = normalizeChannels(channels);
      return normalizedChannels.map(channel =>
        channel.startsWith('channels.') ? channel : `channels.${channel}`,
      );
    },
    [normalizeChannels],
  );
}

export const isWidgetRelatedChannel = (channel: string) => {
  const channelWithoutPrefix = channel.replace('channels.', '');

  return (
    channelWithoutPrefix === 'widget' ||
    channelWithoutPrefix === 'sunshine' ||
    channelWithoutPrefix === 'kustomer_sms' ||
    channelWithoutPrefix === 'intercom_sms'
  );
};

/**
 * Resolve a channel selection, expanding the "widget" umbrella token to the
 * org's other enabled widget-family channels. Deduped, since a selection
 * may already contain family members (e.g. ['widget', 'sunshine']).
 */
export const expandWidgetUmbrella = (
  channels: string[],
  defaultChannels: string[],
): string[] => {
  if (channels.length === 0) {
    return defaultChannels;
  }
  if (!channels.includes('widget')) {
    return channels;
  }
  return [
    ...new Set([
      ...channels.filter(channel => channel !== 'widget'),
      ...defaultChannels.filter(isWidgetRelatedChannel),
    ]),
  ];
};
